import { FC } from 'react';
import { useWizard } from 'react-use-wizard';
import { Card } from '@/components/ui/card';
import { InfoBar } from '@/components/ui/info-bar';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { useForm } from '@/forms/hooks/use-form';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import { NewUnitWizardFooter } from '../new-unit-wizard-footer';

export type UnitDetailsStepProps = {
  unitName: string | undefined;
  readOnly?: boolean;
  onCancel: () => void;
  onNextStep: (formValues: { unitName: string }) => void;
};

export const UnitDetailsStep: FC<UnitDetailsStepProps> = ({
  unitName,
  readOnly,
  onCancel,
  onNextStep,
}) => {
  const { nextStep } = useWizard();
  const { fieldItems, handleSubmit } = useForm<{ unitName: string }>({
    schema: {
      unitName: {
        type: readOnly ? 'readonly' : 'text',
        label: 'Unit Name',
        placeholder: 'Enter a name for this unit',
        value: unitName,
        validation: { required: true, maxLength: 64 },
      },
    },
    onSubmit: (data) => {
      nextStep();
      onNextStep(data);
    },
  });

  const handleNext = () => {
    handleSubmit();
  };

  return (
    <>
      <SectionContainerLayout horizontal className="m-0 border-none p-0">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle="Assign a unique identifier to this unit for easy reference within
            the platform."
          title="Enter Unit Details"
        />
        <Card className="flex flex-col gap-8">
          {fieldItems}
          <InfoBar text="This ID will act as the unit's name. Avoid using personal or sensitive information." />
        </Card>
      </SectionContainerLayout>
      <NewUnitWizardFooter onCancel={onCancel} onNext={handleNext} />
    </>
  );
};
